import { useCallback } from 'react';
import {
  useGetPatientFormsQuery,
  useGetFormDetailQuery,
  useGetAvailableFormsQuery,
  useAssignFormsToPatientMutation,
  useDeleteFormMutation,
} from './api';
import {
  GetFormsRequest,
  GetFormDetailRequest,
  GetAvailableFormsRequest,
  AssignFormsRequest,
  DeleteFormRequest,
} from '../types';

/**
 * Hook to get patient forms with optional callback support
 */
export function usePatientForms(
  args: GetFormsRequest,
  options?: {
    skip?: boolean;
    pollingInterval?: number;
    refetchOnFocus?: boolean;
  },
) {
  const result = useGetPatientFormsQuery(args, {
    skip: options?.skip,
    pollingInterval: options?.pollingInterval || 0,
    refetchOnFocus: options?.refetchOnFocus || false,
  });

  return {
    data: result.data?.forms || [],
    total: result.data?.total || 0,
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to get form detail with optional callback support
 */
export function useFormDetail(
  args: GetFormDetailRequest,
  options?: {
    skip?: boolean;
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
  },
) {
  const result = useGetFormDetailQuery(args, {
    skip: options?.skip,
  });

  // Handle success/error callbacks
  if (result.isSuccess && result.data && options?.onSuccess) {
    options.onSuccess(result.data.form_detail);
  }
  if (result.isError && options?.onError) {
    options.onError(result.error);
  }

  return {
    data: result.data?.form_detail || null,
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to get available forms with optional callback support
 */
export function useAvailableForms(
  args: GetAvailableFormsRequest,
  options?: {
    skip?: boolean;
    onSuccess?: (data: any) => void;
    onError?: (error: any) => void;
  },
) {
  const result = useGetAvailableFormsQuery(args, {
    skip: options?.skip,
  });

  // Handle success/error callbacks
  if (result.isSuccess && result.data && options?.onSuccess) {
    options.onSuccess(result.data);
  }
  if (result.isError && options?.onError) {
    options.onError(result.error);
  }

  return {
    data: result.data?.forms || [],
    pagination: result.data?.pagination || { total: 0, skip: 0, limit: 10 },
    isLoading: result.isLoading,
    isError: result.isError,
    isFetching: result.isFetching,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to assign forms to a patient with onSuccess/onError support
 */
export function useAssignForms() {
  const [assignForms, result] = useAssignFormsToPatientMutation();

  const mutate = useCallback(
    (
      args: AssignFormsRequest,
      options?: { onSuccess?: () => void; onError?: (error: any) => void },
    ) => {
      const promise = assignForms(args);

      // Handle options manually since the hook doesn't directly expose them
      if (options) {
        promise
          .unwrap()
          .then(() => options.onSuccess?.())
          .catch(error => options.onError?.(error));
      }

      return promise;
    },
    [assignForms],
  );

  return {
    mutate,
    isLoading: result.isLoading,
    isError: result.isError,
    isSuccess: result.isSuccess,
    error: result.error,
    reset: result.reset,
  };
}

/**
 * Hook to delete a form with onSuccess/onError support
 */
export function useDeleteForm() {
  const [deleteForm, result] = useDeleteFormMutation();

  const mutate = useCallback(
    (
      args: DeleteFormRequest,
      options?: { onSuccess?: () => void; onError?: (error: any) => void },
    ) => {
      const promise = deleteForm(args);

      // Handle options manually since the hook doesn't directly expose them
      if (options) {
        promise
          .unwrap()
          .then(() => options.onSuccess?.())
          .catch(error => options.onError?.(error));
      }

      return promise;
    },
    [deleteForm],
  );

  return {
    mutate,
    isLoading: result.isLoading,
    isError: result.isError,
    isSuccess: result.isSuccess,
    error: result.error,
    reset: result.reset,
  };
}
